import React, { useMemo, useEffect, useState, useRef, Key } from 'react'
import { getProducts } from '../../../hooks/useProductsCustomHook'
import { generateId } from '../../../utils/helpers'
import TextInput from '../TextInput/TextInput'
import DeleteButton from '../../../shared/Buttons/DeleteButton'
import styles from './ProductsInput.module.scss'
import cn from 'classnames'
import AccountForm from '../../../types/account-form.types'
import { State, actions, Dispatch } from '../../../state-actions-reducers'
import BackButtonImg from '../../../images/arrow-left.svg'
import CheckProductIcon from '../../../images/checkProductIcon.svg'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import { INSTANCE } from '../../../server-parameters'
import { Lang, OptionTree } from '../../../types/common-types'
import SelectTreeInput from '../SelectTreeInput/SelectTreeInput'
import CheckBoxInput from '../CheckBoxInput/CheckBoxInput'

// TODO: set up fonts. Current is too big ang probably is from wrong family

interface Category {
  guiD_Roditelya: string | null;
  guid: string;
  isOpen: boolean;
  name: string;
  nodes: Category[] | null;
}

interface Product {
  categoryGuids?: string[]
  externalId?: string | null;
  guid: string
  inStock?: boolean
  title: string
}

interface Props {
  lang: Lang
  onChange: (value: {title:string, guid:string}[]) => void
  initialValue: {title:string, guid:string}[]
  accountForm: AccountForm
  valid: boolean
  required?: boolean
  state: State
  dispatch: Dispatch
}

export default function ProductsInput({
  state,
  lang,
  accountForm,
  onChange,
  initialValue,
  valid,
  required,
  dispatch,
}: Props) {
  const id = useMemo(generateId, [])
  const [inputValues, setinputValues] = useState<Product[]>([])

  const inputItem = inputValues.map((item, index) => {
    return (
      <li key={index}>
        <span>{item.title}</span>
        <DeleteButton
          onClick={() => {
            setinputValues(inputValues.filter((item, key) => key !== index))
          }}
        />
      </li>
    )
  })

  const [categories, setCategories] = useState<Category[]>([])
  const [products, setProducts] = useState<Product[]>([])

  useMemo(() => {
    getProducts(lang, body => {
      const mainCategory = body?.categories?.[0]?.nodes;
      if (mainCategory?.length > 0)
        setCategories(INSTANCE === 'ala' ? mainCategory?.find((n: Category) => n.name === 'Аренда')?.nodes?.filter((n: Category) => n.name !== 'New') : mainCategory?.filter((n: Category) => n.name !== 'New'))
      setProducts(body?.catalogItems)
    })
  }, [])
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>()
  const [activeMainCategory, setActiveMainCategory] = useState<string>()
  const [activeSubCategory, setActiveSubCategory] = useState<string>()
  const getCategoriesTree = (categories: Category[]) => {
    return categories?.map((category: Category, index: number) => {
      return (
        <li
          key={index}
          className={activeCategory === category.guid ? styles.active : ''}
          onClick={() => {
            setActiveMainCategory(category.guid)
            setActiveCategory(category.guid)
            setActiveSubCategory(undefined)
          }}
        >
          <span>{category.name}</span>
        </li>
      )
    })
  }
  const getSubCategoriesTree = (categories: Category[]) => {
    if (!activeMainCategory) return null;

    const activeCategory = categories.find(c => c.guid === activeMainCategory);
    if (!activeCategory?.nodes) return null;

    return activeCategory.nodes.map((subCategory, index) => (
      <li
        key={`${subCategory.guid}-${index}`}
        className={activeSubCategory === subCategory.guid ? styles.active : ''}
        onClick={() => {
          setActiveSubCategory(subCategory.guid);
          setActiveCategory(subCategory.guid);
        }}
      >
        <span>{subCategory.name}</span>
      </li>
    ));
  };
  // Рекурсивно ищем нужную категорию в дереве
  function findCategory(nodes: Category[], targetGuid: string): Category | null {
    for (const node of nodes) {
      if (node.guid === targetGuid) {
        return node;
      }

      if (node.nodes && node.nodes.length > 0) {
        const found = findCategory(node.nodes, targetGuid);
        if (found) return found;
      }
    }

    return null;
  }

  function findProductsByCategories(categoryGuid: string, categories: Category[], catalogItems: Product[]) {
    // Находим все GUID категорий, включая подкатегории
    const categoryGuids = new Set<string>();

    // Рекурсивная функция для сбора всех GUID подкатегорий
    function collectCategoryGuids(category: Category) {
      categoryGuids.add(category.guid);
      if (category.nodes && category.nodes.length > 0) {
        category.nodes.forEach(collectCategoryGuids);
      }
    }

    // Находим целевую категорию
    const targetCategory = findCategory(categories, categoryGuid);
    console.log('targetCategory', targetCategory);

    if (!targetCategory) {
      console.log('Category not found');
      return [];
    }

    // Собираем все GUID категорий и подкатегорий
    collectCategoryGuids(targetCategory);

    // Находим товары, которые принадлежат любой из этих категорий
    const itemsInCategory = catalogItems.filter((item: Product) =>
      item.categoryGuids && item.categoryGuids.some((guid: string) => categoryGuids.has(guid))
    );

    return itemsInCategory;
  }
  // Функция для фильтрации товаров по поисковому запросу
  const filteredProducts = useMemo(() => {
    if (!searchQuery) return products;
    return products.filter(product =>
      product.title.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [products, searchQuery]);

  // Функция для получения категорий, содержащих отфильтрованные товары
  const getFilteredCategories = useMemo(() => {
    if (!searchQuery) return categories;

    return categories.filter(category => {
      // Проверяем товары в текущей категории
      const categoryProducts = findProductsByCategories(category.guid, categories, filteredProducts);
      if (categoryProducts.length > 0) return true;

      // Проверяем подкатегории
      if (category.nodes) {
        return category.nodes.some(subCategory => {
          const subCategoryProducts = findProductsByCategories(subCategory.guid, categories, filteredProducts);
          return subCategoryProducts.length > 0;
        });
      }

      return false;
    });
  }, [categories, filteredProducts, searchQuery]);

  // Обновленные функции для отображения дерева категорий
  const categoriesTree = useMemo(() => {
    return getCategoriesTree(searchQuery ? getFilteredCategories : categories);
  }, [categories, activeCategory, searchQuery, getFilteredCategories]);

  const categoriesSubTree = useMemo(() => {
    if (!activeMainCategory) return null;

    const activeCategory = categories.find(c => c.guid === activeMainCategory);
    if (!activeCategory?.nodes) return null;

    // В режиме поиска показываем только подкатегории с товарами, соответствующими запросу
    if (searchQuery) {
      return activeCategory.nodes
        .filter(subCategory => {
          const productsInSubCategory = findProductsByCategories(
            subCategory.guid,
            categories,
            products // Используем все товары, не filteredProducts
          );

          // Фильтруем товары подкатегории по поисковому запросу
          const matchingProducts = productsInSubCategory.filter(product =>
            product.title.toLowerCase().includes(searchQuery.toLowerCase())
          );

          return matchingProducts.length > 0;
        })
        .map((subCategory, index) => (
          <li
            key={`${subCategory.guid}-${index}`}
            className={activeSubCategory === subCategory.guid ? styles.active : ''}
            onClick={() => {
              setActiveSubCategory(subCategory.guid);
              setActiveCategory(subCategory.guid);
            }}
          >
            <span>{subCategory.name}</span>
          </li>
        ));
    }

    // Без поиска - показываем все подкатегории
    return activeCategory.nodes.map((subCategory, index) => (
      <li
        key={`${subCategory.guid}-${index}`}
        className={activeSubCategory === subCategory.guid ? styles.active : ''}
        onClick={() => {
          setActiveSubCategory(subCategory.guid);
          setActiveCategory(subCategory.guid);
        }}
      >
        <span>{subCategory.name}</span>
      </li>
    ));
  }, [categories, activeMainCategory, activeSubCategory, searchQuery, products]);

  // Вынесем рендер товара в отдельную функцию
  const renderProductItem = (product: Product) => {
    const isSelected = inputValues.some(e => e.guid === product.guid);
    return (
      <li
        className={cn(isSelected && styles.active)}
        key={product.guid}
        onClick={() => handleProductClick(product, isSelected)}
      >
        <CheckBoxInput className={styles.checkBox} value={isSelected} />
        <span>{product.title}</span>
      </li>
    );
  };

  const productsList = useMemo(() => {
    // Базовый список товаров в зависимости от выбранной категории/подкатегории
    let productsToShow: Product[] = [];

    if (activeSubCategory) {
      // Если выбрана подкатегория - берем товары только этой подкатегории
      productsToShow = findProductsByCategories(activeSubCategory, categories, products);
    }
    else if (activeCategory) {
      // Если выбрана только категория - берем товары категории и всех подкатегорий
      productsToShow = findProductsByCategories(activeCategory, categories, products);
    }
    else {
      // Если ничего не выбрано - пустой список
      return [];
    }

    // Применяем поисковый фильтр, если есть запрос
    if (searchQuery) {
      productsToShow = productsToShow.filter(product =>
        product.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return productsToShow.map(renderProductItem);
  }, [products, activeCategory, activeSubCategory, inputValues, searchQuery, categories]);

  const handleProductClick = (product: Product, isSelected: boolean) => {
    if (!isSelected) {
      if (inputValues.length < 10) {
        setinputValues(data => [...data, { title: product.title, guid: product.guid }]);
      }
    } else {
      setinputValues(inputValues.filter(e => e.guid !== product.guid));
    }
  };

  // Инициализация только при первом получении products
  useEffect(() => {
    if (products.length > 0 && state.goods.length > 0 && inputValues.length === 0) {
      const initialProducts = products.filter((e: Product) =>
        (state.goods as string[]).includes(e.guid)
      );
      setinputValues(initialProducts);
    }
  }, [products, state.goods]);

  // Отдельный эффект для изменений пользователя
  useEffect(() => {
    if (inputValues.length > 0) {
      onChange(inputValues.map((e) => {return {title:e.title, guid: e.guid}}));
    }
  }, [inputValues]);

  const [mobileTree, setMobileTree] = useState(<></>)

  useEffect(() => {
    setMobileTree(<ul className={styles.productsUl}>{productsList}</ul>)
  }, [activeCategory, categoriesTree, inputValues])

  const [anotherValue, setAnotherValue] = useState('')
  function transformCategoriesToOptionTree(categories: any[]): OptionTree[] {
    return categories.map(category => {
      const result: OptionTree = {
        value: category.guid,
        label: category.name,
        children: category.nodes
      };

      if (category.nodes && category.nodes.length > 0) {
        result.children = transformCategoriesToOptionTree(category.nodes);
      }

      return result;
    });
  }
  const transformedData = transformCategoriesToOptionTree(categories);
  // Обработчик изменения поискового запроса
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.startsWith(' ')) return;
    setSearchQuery(e.target.value);
  };

  return (
    <div className="row">
      <div className="col-12 col-xs-12">
        <label htmlFor="" className={styles.searchInputBox}>
          <input
            type="text"
            placeholder={accountForm?.searchInput[lang]}
            onChange={handleSearchChange}
            value={searchQuery}
          />
        </label>
        <div className={styles.productsContainer}>
          <div className={styles.productsContainerRow}>
            <div className="col-4 col-xs-12">
              <ul>{categoriesTree}</ul>
            </div>
            <div className="col-4 col-xs-12">
              <ul>{categoriesSubTree}</ul>
            </div>
            <div className="col-4 col-xs-12">
              <ul className={styles.productsUl}>{productsList}</ul>
            </div>
          </div>
        </div>
        <div className={styles.productsContainerMobile}>
          <div className={styles.productsContainerMobileRow}>
            <SelectTreeInput content={accountForm.selectCategoriesOptions} lang={lang} options={transformedData} onChange={(e) => setActiveCategory(e)} />
            <div className={styles.productsMobileBox + ' ' + 'col-12'}>
              {mobileTree}
            </div>
          </div>
        </div>
      </div>
      <div className="col-12 col-xs-12">
        <p className={styles.labelText}>
          {accountForm.productsInputText.label[lang]}
          {required && <span className={styles.required}></span>}
        </p>
        <div className={styles.inputContainer}>
          <ul>{inputItem}</ul>
        </div>
      </div>
      <div className="col-6 col-xs-12">
        <label htmlFor="" className={styles.otherInputBox}>
          <p>{accountForm.otherProducts.label[lang]}</p>
          <input
            type="text"
            onKeyPress={event => {
              if (event.charCode === 13) {
                if (inputValues.length < 10) {
                  setinputValues(inputValues => [
                    ...inputValues,
                    { title: anotherValue, guid: '' },
                  ])
                  setAnotherValue('')
                }
              }
            }}
            placeholder={accountForm?.otherProducts?.placeholder?.[lang]}
            onChange={(e) => {
              if (e.target.value.startsWith(' ')) {
                return
              }
              setAnotherValue(e.target.value)
            }}
            value={anotherValue}
          />
        </label>
      </div>
      <div className="col-6 col-xs-12">
        <TextInput
          testName="feedback"
          testErrorName="error-feedback"
          disabled={!state.agreed}
          lang={lang}
          content={accountForm.feedback}
          onChange={feedback =>
            dispatch({
              type: actions.FEEDBACK,
              data: feedback,
            })
          }
          value={state.feedback || ''}
          valid={true}
        />
      </div>
    </div>
  )
}
